import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTrips, getStopsByTripId, getTripSchedules, getStopsByScheduleId } from '../../services/tripsService';
import { createBookingRequest } from '../../services/bookingRequestsService';
import { getCurrentUser } from '../../services/authService';
import { searchCities } from '../../services/locationService';
import './TruckSearch.css';

const TruckSearch = ({ isAuthenticated, onCustomerLogin }) => {
  const navigate = useNavigate();
  const [searchData, setSearchData] = useState({
    fromLocation: '',
    fromLocationCityId: null,
    toLocation: '',
    toLocationCityId: null
  });
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showBookingPopup, setShowBookingPopup] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [products, setProducts] = useState([
    {
      productName: '',
      productCategory: '',
      customCategory: '',
      length: '',
      height: '',
      width: '',
      weight: ''
    }
  ]);
  const [bookingFormData, setBookingFormData] = useState({
    phoneNumber: '',
    alternatePhone: '',
    specialInstructions: ''
  });
  const [bookingFormErrors, setBookingFormErrors] = useState({});
  const [showBookingSuccess, setShowBookingSuccess] = useState(false);
  
  // City autocomplete states
  const [fromCitySuggestions, setFromCitySuggestions] = useState([]);
  const [toCitySuggestions, setToCitySuggestions] = useState([]);
  const [showFromSuggestions, setShowFromSuggestions] = useState(false);
  const [showToSuggestions, setShowToSuggestions] = useState(false);
  const [isSearchingCities, setIsSearchingCities] = useState(false);
  const [isSubmittingBooking, setIsSubmittingBooking] = useState(false);

  // Mock available trucks data
  const mockAvailableTrucks = [
    {
      id: 'TRK001',
      vehicleNumber: 'TN10AB1234',
      vehicleType: '16 Wheeler',
      capacity: '20 tons',
      driverName: 'Rajesh Kumar',
      driverPhone: '+91 98765 43210',
      currentLocation: 'Chennai',
      status: 'Available',
      estimatedArrival: '2 hours'
    },
    {
      id: 'TRK002',
      vehicleNumber: 'KA01CD5678',
      vehicleType: '14 Wheeler',
      capacity: '15 tons',
      driverName: 'Suresh Singh',
      driverPhone: '+91 98765 43211',
      currentLocation: 'Bangalore',
      status: 'Available',
      estimatedArrival: '3 hours'
    },
    {
      id: 'TRK003',
      vehicleNumber: 'MH02EF9012',
      vehicleType: '12 Wheeler',
      capacity: '12 tons',
      driverName: 'Amit Patel',
      driverPhone: '+91 98765 43212',
      currentLocation: 'Mumbai',
      status: 'Available',
      estimatedArrival: '4 hours'
    }
  ];

  // Function to map vehicle type to "Lorry" or "Truck"
  const getVehicleTypeDisplay = (vehicleType) => {
    if (!vehicleType) return 'Truck';
    const type = vehicleType.toLowerCase();
    // Check if it contains "lorry" or is a smaller vehicle type
    if (type.includes('lorry') || type.includes('12') || type.includes('14')) {
      return 'Lorry';
    }
    // Default to "Truck" for larger vehicles
    return 'Truck';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Trigger city search for autocomplete
    if (name === 'fromLocation' && value.length >= 2) {
      searchCitiesForAutocomplete(value, 'from');
    } else if (name === 'fromLocation' && value.length < 2) {
      setFromCitySuggestions([]);
      setShowFromSuggestions(false);
    }
    
    if (name === 'toLocation' && value.length >= 2) {
      searchCitiesForAutocomplete(value, 'to');
    } else if (name === 'toLocation' && value.length < 2) {
      setToCitySuggestions([]);
      setShowToSuggestions(false);
    }
  };

  // Search cities for autocomplete
  const searchCitiesForAutocomplete = async (query, type) => {
    if (!query || query.trim().length < 2) {
      if (type === 'from') {
        setFromCitySuggestions([]);
        setShowFromSuggestions(false);
      } else {
        setToCitySuggestions([]);
        setShowToSuggestions(false);
      }
      return;
    }

    try {
      setIsSearchingCities(true);
      // Backend maximum limit is 100, so we use 100 to show maximum cities
      // When user searches for a district name like "virudhunagar", 
      // this will show up to 100 cities from that district
      const response = await searchCities({ q: query, limit: 100 });
      
      // Handle different response structures from server
      // response could be: { data: [...] }, { cities: [...] }, { items: [...] }, or directly an array
      let cities = [];
      if (Array.isArray(response)) {
        cities = response;
      } else if (response && Array.isArray(response.data)) {
        cities = response.data;
      } else if (response && Array.isArray(response.cities)) {
        cities = response.cities;
      } else if (response && Array.isArray(response.items)) {
        cities = response.items;
      } else if (response && response.data && Array.isArray(response.data.items)) {
        cities = response.data.items;
      } else {
        // Fallback: ensure cities is always an array
        cities = [];
        console.warn('Unexpected response structure from searchCities:', response);
      }
      
      if (type === 'from') {
        setFromCitySuggestions(cities);
        setShowFromSuggestions(cities.length > 0);
      } else {
        setToCitySuggestions(cities);
        setShowToSuggestions(cities.length > 0);
      }
    } catch (error) {
      console.error('Error searching cities:', error);
      // On error, hide suggestions
      if (type === 'from') {
        setFromCitySuggestions([]);
        setShowFromSuggestions(false);
      } else {
        setToCitySuggestions([]);
        setShowToSuggestions(false);
      }
    } finally {
      setIsSearchingCities(false);
    }
  };

  // Handle city selection from autocomplete
  const handleCitySelect = (city, type) => {
    if (type === 'from') {
      setSearchData(prev => ({
        ...prev,
        fromLocation: city.display_name || `${city.city}, ${city.district || ''}, ${city.state || ''}`,
        fromLocationCityId: city.id
      }));
      setFromCitySuggestions([]);
      setShowFromSuggestions(false);
    } else {
      setSearchData(prev => ({
        ...prev,
        toLocation: city.display_name || `${city.city}, ${city.district || ''}, ${city.state || ''}`,
        toLocationCityId: city.id
      }));
      setToCitySuggestions([]);
      setShowToSuggestions(false);
    }
  };

  // Helper function to search for available trucks
  const searchAvailableTrucks = async (fromLocation, toLocation) => {
    setIsSearching(true);
    try {
      // Fetch trips with distance calculation from backend
      // Pass city_id parameters if available for distance calculation
      const tripParams = { 
        limit: 100, // API maximum limit
        include_distance: true // Request distance in response
      };
      
      // Add city_id parameters if available (for distance calculation)
      if (searchData.fromLocationCityId) {
        tripParams.from_city_id = searchData.fromLocationCityId;
      }
      if (searchData.toLocationCityId) {
        tripParams.to_city_id = searchData.toLocationCityId;
      }
      
      // Fetch both trips and trip schedules in parallel
      const [tripsResponse, schedulesResponse] = await Promise.all([
        getTrips(tripParams),
        getTripSchedules({ 
          limit: 100,
          trip_status: 'Scheduled' // Only active schedules
        })
      ]);
      
      const response = tripsResponse;
      
      // Debug: Log API request and response
      console.log('🔍 API Request Params:', tripParams);
      console.log('📦 API Response:', response);
      
      // Extract trips data from response
      let tripsData = [];
      if (response.data) {
        tripsData = response.data.items || response.data.trips || response.data.data || [];
      } else if (Array.isArray(response)) {
        tripsData = response;
      } else if (response.items || response.trips) {
        tripsData = response.items || response.trips;
      }
      
      // Extract trip schedules data from response
      let schedulesData = [];
      if (schedulesResponse.data) {
        schedulesData = schedulesResponse.data.items || schedulesResponse.data.schedules || schedulesResponse.data.data || [];
      } else if (Array.isArray(schedulesResponse)) {
        schedulesData = schedulesResponse;
      } else if (schedulesResponse.items || schedulesResponse.schedules) {
        schedulesData = schedulesResponse.items || schedulesResponse.schedules;
      }
      
      // Debug: Log first trip to see what fields are available
      if (tripsData.length > 0) {
        console.log('🚛 First trip data:', tripsData[0]);
        console.log('📏 Distance fields in trip:', {
          route_distance_km: tripsData[0].route_distance_km,
          route_distance: tripsData[0].route_distance,
          distance_km: tripsData[0].distance_km,
          distance: tripsData[0].distance
        });
      }

      // Helper function to extract city/district names from location strings
      const extractLocationKeywords = (location) => {
        if (!location) return [];
        const lower = location.toLowerCase();
        // Split by comma and clean up
        const parts = lower.split(',').map(p => p.trim()).filter(p => p.length > 0);
        // Return all parts as keywords (city, district, state, etc.)
        return parts;
      };

      // Helper function to check if two locations match (fuzzy matching)
      const locationsMatch = (searchLocation, tripLocation) => {
        if (!searchLocation || !tripLocation) return false;
        
        // Exact match (case-insensitive)
        if (searchLocation.toLowerCase() === tripLocation.toLowerCase()) {
          return true;
        }
        
        // Extract keywords from both locations
        const searchKeywords = extractLocationKeywords(searchLocation);
        const tripKeywords = extractLocationKeywords(tripLocation);
        
        // Check if any significant keyword from search appears in trip location
        // (Skip very short keywords like "ct", "in", etc.)
        const significantSearchKeywords = searchKeywords.filter(k => k.length >= 3);
        
        if (significantSearchKeywords.length === 0) return false;
        
        // Check if at least one significant keyword matches
        return significantSearchKeywords.some(searchKeyword => {
          // Check if keyword appears in any part of trip location
          return tripKeywords.some(tripKeyword => 
            tripKeyword.includes(searchKeyword) || searchKeyword.includes(tripKeyword)
          );
        });
      };

      // Enhanced location matching function with fuzzy matching
      const matchesLocation = (trip, fromLocation, toLocation) => {
        // Get stops data - could be in different formats
        // For trips: trip_stops, stops, stopsData
        // For schedules: trip_schedule_stops, stops, stopsData
        const stops = trip.stopsData || trip.stops || trip.trip_stops || trip.trip_schedule_stops || [];
        
        // Check if fromLocation matches start or any stop (fuzzy match)
        const fromMatches = 
          locationsMatch(fromLocation, trip.start_location) ||
          stops.some(stop => {
            const stopLocation = typeof stop === 'object' 
              ? (stop.stop_location || stop.location || stop.name || stop.stop_name) 
              : stop;
            return locationsMatch(fromLocation, stopLocation);
          });
        
        // Check if toLocation matches destination or any stop after fromLocation
        const toMatches = 
          locationsMatch(toLocation, trip.destination) ||
          stops.some((stop, index) => {
            const stopLocation = typeof stop === 'object' 
              ? (stop.stop_location || stop.location || stop.name || stop.stop_name) 
              : stop;
            const matchesTo = locationsMatch(toLocation, stopLocation);
            
            // If fromLocation is a stop, ensure toLocation comes after it
            if (fromMatches && !locationsMatch(fromLocation, trip.start_location)) {
              // Find index of fromLocation stop
              const fromStopIndex = stops.findIndex(s => {
                const sl = typeof s === 'object' ? (s.stop_location || s.location || s.name || s.stop_name) : s;
                return locationsMatch(fromLocation, sl);
              });
              return matchesTo && index > fromStopIndex;
            }
            return matchesTo;
          });
        
        return fromMatches && toMatches;
      };

      // IMPORTANT: Fetch stops for ALL trips FIRST (before filtering) if not already included
      // This ensures stops are available for location matching during filtering
      const tripsWithStopsFetched = await Promise.all(
        tripsData.map(async (trip) => {
          // Check if stops are already in trip data
          const hasStops = trip.stopsData || trip.stops || trip.trip_stops || trip.stops_list;
          
          if (!hasStops) {
            try {
              // Fetch stops for this trip
              const tripId = trip.id; // Use numeric primary key
              if (tripId) {
                const stopsResponse = await getStopsByTripId(tripId);
                // Attach stops to trip object
                const stops = stopsResponse.data || stopsResponse.items || stopsResponse || [];
                trip.trip_stops = stops;
                trip.stops = stops;
                trip.stopsData = stops;
              }
            } catch (error) {
              console.warn(`Failed to fetch stops for trip ${trip.id} (${trip.trip_id}):`, error);
              // Continue without stops if fetch fails
              trip.trip_stops = [];
              trip.stops = [];
              trip.stopsData = [];
            }
          }
          
          return trip;
        })
      );

      // IMPORTANT: Fetch stops for ALL schedules FIRST (before filtering)
      // This ensures stops are available for location matching during filtering
      const schedulesWithStopsFetched = await Promise.all(
        schedulesData.map(async (schedule) => {
          // Check if stops are already in schedule data
          const hasStops = schedule.stopsData || schedule.stops || schedule.trip_schedule_stops || schedule.stops_list;
          
          if (!hasStops) {
            try {
              // Fetch stops for this schedule
              const scheduleId = schedule.id; // Use numeric primary key
              if (scheduleId) {
                const stopsResponse = await getStopsByScheduleId(scheduleId);
                // Attach stops to schedule object
                const stops = stopsResponse.data || stopsResponse.items || stopsResponse || [];
                schedule.trip_schedule_stops = stops;
                schedule.stops = stops;
                schedule.stopsData = stops;
              }
            } catch (error) {
              console.warn(`Failed to fetch stops for schedule ${schedule.id} (${schedule.schedule_id}):`, error);
              // Continue without stops if fetch fails
              schedule.trip_schedule_stops = [];
              schedule.stops = [];
              schedule.stopsData = [];
            }
          }
          
          return schedule;
        })
      );

      // Filter trips by:
      // 1. Enhanced location matching (start/destination OR intermediate stops) - NOW WITH STOPS AVAILABLE
      // 2. Only partial load trips (or undefined for backward compatibility)
      // 3. Active status (Scheduled or In Progress)
      const filteredTrips = tripsWithStopsFetched.filter(trip => {
        const locationMatch = matchesLocation(trip, fromLocation, toLocation);
        
        const hasAvailableCapacity = 
          trip.load_type === 'partial load' || 
          trip.load_type === null || 
          trip.load_type === undefined ||
          (trip.remaining_capacity && trip.remaining_capacity > 0);
        
        const isActive = 
          trip.trip_status === 'Scheduled' || 
          trip.trip_status === 'In Progress';
        
        return locationMatch && hasAvailableCapacity && isActive;
      });
      
      // Filter trip schedules by:
      // 1. Enhanced location matching (start/destination OR intermediate stops) - NOW WITH STOPS AVAILABLE
      // 2. Only partial load schedules (or undefined for backward compatibility)
      // 3. Active status (Scheduled or Active)
      const filteredSchedules = schedulesWithStopsFetched.filter(schedule => {
        const locationMatch = matchesLocation(schedule, fromLocation, toLocation);
        
        const hasAvailableCapacity = 
          schedule.load_type === 'partial load' || 
          schedule.load_type === null || 
          schedule.load_type === undefined ||
          (schedule.total_capacity && schedule.used_capacity !== undefined && 
           (schedule.total_capacity - schedule.used_capacity) > 0);
        
        const isActive = 
          schedule.trip_status === 'Scheduled' || 
          schedule.trip_status === 'Active';
        
        return locationMatch && hasAvailableCapacity && isActive;
      });

      // Helper function to build full route string with ALL intermediate stops
      const buildRouteString = (trip, fromLocation, toLocation) => {
        // Get stops from various possible fields in API response
        // For trips: trip_stops, stops, stopsData, stops_list
        // For schedules: trip_schedule_stops, stops, stopsData, stops_list
        const stops = trip.stopsData || trip.stops || trip.trip_stops || trip.trip_schedule_stops || trip.stops_list || [];
        
        // Check if locations are intermediate stops (using fuzzy matching)
        const fromIsStop = !locationsMatch(fromLocation, trip.start_location);
        const toIsStop = !locationsMatch(toLocation, trip.destination);
        
        // Sort stops by sequence if available
        const sortedStops = [...stops].sort((a, b) => {
          const seqA = typeof a === 'object' ? (a.stop_sequence || a.sequence || a.order || 0) : 0;
          const seqB = typeof b === 'object' ? (b.stop_sequence || b.sequence || b.order || 0) : 0;
          return seqA - seqB;
        });
        
        // Build route parts
        const routeParts = [];
        
        // Add start location
        routeParts.push(trip.start_location || fromLocation);
        
        // Add ALL intermediate stops (show complete route)
        sortedStops.forEach((stop) => {
          const stopLocation = typeof stop === 'object' 
            ? (stop.stop_location || stop.location || stop.name || stop.stop_name) 
            : stop;
          
          // Include ALL stops in the route display
          if (stopLocation && stopLocation.trim()) {
            routeParts.push(stopLocation);
          }
        });
        
        // Add destination
        routeParts.push(trip.destination || toLocation);
        
        // Build route string
        let routeString = routeParts.join(' → ');
        
        return routeString;
      };

      // Trips already have stops fetched above, so we can use filteredTrips directly
      const tripsWithStops = filteredTrips;
      
      // Schedules already have stops fetched above, so we can use filteredSchedules directly
      const schedulesWithStops = filteredSchedules;

      // Map trips to truck card format
      const trucksFromTrips = tripsWithStops.map(trip => {
        const vehicle = trip.vehicle || {};
        const driver = trip.driver || {};
        
        // Calculate capacity display
        let capacityDisplay = 'N/A';
        if (trip.remaining_capacity) {
          capacityDisplay = `${trip.remaining_capacity} ${trip.weight_unit || 'kg'}`;
        } else if (trip.total_capacity) {
          capacityDisplay = `${trip.total_capacity} ${trip.weight_unit || 'kg'}`;
        } else if (vehicle.capacity_kg) {
          capacityDisplay = `${vehicle.capacity_kg} kg`;
        }

        // Build full route string (now includes stops)
        const fullRoute = buildRouteString(trip, fromLocation, toLocation);
        
        // Check if pickup/delivery is at intermediate stop
        const pickupIsIntermediate = !locationsMatch(fromLocation, trip.start_location);
        const deliveryIsIntermediate = !locationsMatch(toLocation, trip.destination);

        // Extract distance from backend response (check multiple possible field names)
        const routeDistance = trip.route_distance_km || trip.route_distance || trip.distance_km || trip.distance || null;

        return {
          id: trip.trip_id || trip.id || `trip-${trip.id}`,
          tripId: trip.trip_id || trip.id,
          vehicleNumber: vehicle.vehicle_number || 'N/A',
          vehicleType: vehicle.vehicle_type || 'Truck',
          capacity: capacityDisplay,
          driverName: driver.name || driver.full_name || 'N/A',
          driverPhone: driver.mobile_number || driver.contact || driver.phone || 'N/A',
          currentLocation: trip.start_location || 'N/A',
          status: trip.trip_status === 'In Progress' ? 'In Transit' : 'Available',
          estimatedArrival: trip.estimated_duration || trip.eta || 'N/A',
          // Route information
          fullRoute: fullRoute,
          routeDistance: routeDistance, // From backend API
          pickupIsIntermediate: pickupIsIntermediate,
          deliveryIsIntermediate: deliveryIsIntermediate,
          // Store trip data for booking
          tripData: trip,
          isSchedule: false // Mark as regular trip
        };
      });
      
      // Map trip schedules to truck card format
      const trucksFromSchedules = schedulesWithStops.map(schedule => {
        const vehicle = schedule.vehicle || {};
        const driver = schedule.driver || {};
        
        // Calculate capacity display for schedules
        let capacityDisplay = 'N/A';
        if (schedule.total_capacity && schedule.used_capacity !== undefined) {
          const remaining = schedule.total_capacity - schedule.used_capacity;
          if (remaining > 0) {
            capacityDisplay = `${remaining} ${schedule.weight_unit || 'kg'}`;
          }
        } else if (schedule.load_weight) {
          capacityDisplay = `${schedule.load_weight} ${schedule.weight_unit || 'kg'}`;
        } else if (vehicle.capacity_kg) {
          capacityDisplay = `${vehicle.capacity_kg} kg`;
        }

        // Build full route string (now includes stops)
        const fullRoute = buildRouteString(schedule, fromLocation, toLocation);
        
        // Check if pickup/delivery is at intermediate stop
        const pickupIsIntermediate = !locationsMatch(fromLocation, schedule.start_location);
        const deliveryIsIntermediate = !locationsMatch(toLocation, schedule.destination);
        
        // Get schedule type display
        const scheduleTypeDisplay = schedule.schedule_type 
          ? schedule.schedule_type.charAt(0).toUpperCase() + schedule.schedule_type.slice(1)
          : 'Scheduled';

        return {
          id: schedule.schedule_id || schedule.id || `schedule-${schedule.id}`,
          tripId: schedule.schedule_id || schedule.id,
          vehicleNumber: vehicle.vehicle_number || 'N/A',
          vehicleType: vehicle.vehicle_type || 'Truck',
          capacity: capacityDisplay,
          driverName: driver.name || driver.full_name || 'N/A',
          driverPhone: driver.mobile_number || driver.contact || driver.phone || 'N/A',
          currentLocation: schedule.start_location || 'N/A',
          status: `${scheduleTypeDisplay} Schedule`, // Show schedule type
          estimatedArrival: schedule.trip_time || 'N/A',
          // Route information
          fullRoute: fullRoute,
          routeDistance: null, // Schedules don't have distance calculated yet
          pickupIsIntermediate: pickupIsIntermediate,
          deliveryIsIntermediate: deliveryIsIntermediate,
          // Store schedule data for booking
          tripData: schedule,
          isSchedule: true, // Mark as schedule
          scheduleName: schedule.schedule_name || schedule.schedule_id,
          scheduleType: schedule.schedule_type
        };
      });
      
      // Combine trips and schedules
      const availableTrucks = [...trucksFromTrips, ...trucksFromSchedules];

      setSearchResults(availableTrucks);
      return availableTrucks;
    } catch (error) {
      console.error('Error searching for available trucks:', error);
      alert('Failed to search for available trucks. Please try again.');
      setSearchResults([]);
      return [];
    } finally {
      setIsSearching(false);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.location-autocomplete-wrapper')) {
        setShowFromSuggestions(false);
        setShowToSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchData.fromLocation || !searchData.toLocation) {
      alert('Please enter both from and to locations');
      return;
    }

    // Check if user is authenticated
    if (!isAuthenticated) {
      // Store search data in localStorage
      localStorage.setItem('pendingSearch', JSON.stringify({
        fromLocation: searchData.fromLocation,
        toLocation: searchData.toLocation,
        timestamp: new Date().toISOString()
      }));
      // Redirect to login
      navigate('/login');
      return;
    }

    // Use the helper function to search for available trucks
    await searchAvailableTrucks(searchData.fromLocation, searchData.toLocation);
  };

  // Get customer phone number from profile
  const getCustomerPhone = () => {
    // Try to get from localStorage (if profile was saved)
    const savedProfile = localStorage.getItem('customerProfile');
    if (savedProfile) {
      try {
        const profile = JSON.parse(savedProfile);
        return profile.mobileNumber || '';
      } catch (error) {
        console.error('Error parsing customer profile:', error);
      }
    }
    // Default phone from CustomerProfile component structure
    return '9876543210'; // Default phone number
  };

  const handleBookTruck = (truck) => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      // Store booking data in localStorage
      const bookingData = {
        truck: truck,
        fromLocation: searchData.fromLocation,
        toLocation: searchData.toLocation,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('pendingBooking', JSON.stringify(bookingData));
      // Redirect to login
      navigate('/login');
      return;
    }
    
    // If authenticated, show booking form
    setSelectedTruck(truck);
    // Initialize form with phone number from profile
    const customerPhone = getCustomerPhone();
    setProducts([{
      productName: '',
      productCategory: '',
      customCategory: '',
      length: '',
      height: '',
      width: '',
      weight: ''
    }]);
    setBookingFormData({
      phoneNumber: customerPhone,
      alternatePhone: '',
      specialInstructions: ''
    });
    setBookingFormErrors({});
    setShowBookingForm(true);
  };

  // Check for pending search and booking after login
  useEffect(() => {
    if (isAuthenticated) {
      // Check for pending search first
      const pendingSearch = localStorage.getItem('pendingSearch');
      if (pendingSearch) {
        const handlePendingSearch = async () => {
          try {
            const searchData = JSON.parse(pendingSearch);
            setSearchData({
              fromLocation: searchData.fromLocation || '',
              toLocation: searchData.toLocation || ''
            });
            // Automatically trigger search
            await searchAvailableTrucks(searchData.fromLocation, searchData.toLocation);
            // Clear pending search
            localStorage.removeItem('pendingSearch');
          } catch (error) {
            console.error('Error parsing pending search:', error);
            localStorage.removeItem('pendingSearch');
          }
        };
        handlePendingSearch();
      }

      // Check for pending booking
      const pendingBooking = localStorage.getItem('pendingBooking');
      if (pendingBooking) {
        try {
          const bookingData = JSON.parse(pendingBooking);
          setSelectedTruck(bookingData.truck);
          setSearchData({
            fromLocation: bookingData.fromLocation || '',
            toLocation: bookingData.toLocation || ''
          });
          // Initialize form with phone number from profile
          const customerPhone = getCustomerPhone();
          setProducts([{
            productName: '',
            productCategory: '',
            customCategory: '',
            length: '',
            height: '',
            width: '',
            weight: ''
          }]);
          setBookingFormData({
            phoneNumber: customerPhone,
            alternatePhone: '',
            specialInstructions: ''
          });
          setShowBookingForm(true);
          // Clear pending booking
          localStorage.removeItem('pendingBooking');
        } catch (error) {
          console.error('Error parsing pending booking:', error);
          localStorage.removeItem('pendingBooking');
        }
      }
    }
  }, [isAuthenticated]);

  const handleBookingFormChange = (e) => {
    const { name, value } = e.target;
    setBookingFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when user starts typing
    if (bookingFormErrors[name]) {
      setBookingFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleProductChange = (index, field, value) => {
    const updatedProducts = [...products];
    updatedProducts[index] = {
      ...updatedProducts[index],
      [field]: value
    };
    setProducts(updatedProducts);
    // Clear errors when user starts typing
    const errorKey = `product_${index}_${field}`;
    if (bookingFormErrors[errorKey]) {
      setBookingFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const handleAddProduct = () => {
    setProducts([...products, {
      productName: '',
      productCategory: '',
      customCategory: '',
      length: '',
      height: '',
      width: '',
      weight: ''
    }]);
  };

  const productCategories = ['oil', 'crackers', 'vegetables', 'steel', 'others'];

  const handleRemoveProduct = (index) => {
    if (products.length > 1) {
      const updatedProducts = products.filter((_, i) => i !== index);
      setProducts(updatedProducts);
      // Clear errors for removed product
      const newErrors = { ...bookingFormErrors };
      Object.keys(newErrors).forEach(key => {
        if (key.startsWith(`product_${index}_`)) {
          delete newErrors[key];
        }
      });
      setBookingFormErrors(newErrors);
    }
  };

  const validateBookingForm = () => {
    const newErrors = {};

    // Validate each product
    products.forEach((product, index) => {
      if (!product.productName.trim()) {
        newErrors[`product_${index}_productName`] = 'Product name is required';
      }

      if (!product.productCategory.trim()) {
        newErrors[`product_${index}_productCategory`] = 'Product category is required';
      }

      if (product.productCategory === 'others' && !product.customCategory.trim()) {
        newErrors[`product_${index}_customCategory`] = 'Please specify the custom category';
      }

      if (!product.length.trim()) {
        newErrors[`product_${index}_length`] = 'Length is required';
      } else if (isNaN(product.length) || parseFloat(product.length) <= 0) {
        newErrors[`product_${index}_length`] = 'Please enter a valid length';
      }

      if (!product.height.trim()) {
        newErrors[`product_${index}_height`] = 'Height is required';
      } else if (isNaN(product.height) || parseFloat(product.height) <= 0) {
        newErrors[`product_${index}_height`] = 'Please enter a valid height';
      }

      if (!product.width.trim()) {
        newErrors[`product_${index}_width`] = 'Width is required';
      } else if (isNaN(product.width) || parseFloat(product.width) <= 0) {
        newErrors[`product_${index}_width`] = 'Please enter a valid width';
      }

      if (!product.weight.trim()) {
        newErrors[`product_${index}_weight`] = 'Weight is required';
      } else if (isNaN(product.weight) || parseFloat(product.weight) <= 0) {
        newErrors[`product_${index}_weight`] = 'Please enter a valid weight';
      }
    });

    // Validate contact information
    if (!bookingFormData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(bookingFormData.phoneNumber.replace(/[\s-]/g, ''))) {
      newErrors.phoneNumber = 'Please enter a valid 10-digit phone number';
    }

    if (bookingFormData.alternatePhone && !/^[0-9]{10}$/.test(bookingFormData.alternatePhone.replace(/[\s-]/g, ''))) {
      newErrors.alternatePhone = 'Please enter a valid 10-digit phone number';
    }

    setBookingFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateBookingForm()) {
      return;
    }

    // Get customer information
    const currentUser = getCurrentUser();
    if (!currentUser) {
      alert('Please log in to submit a booking request.');
      return;
    }
    
    const customerId = currentUser.customer_id || currentUser.customerId || null;
    if (!customerId) {
      alert('Customer ID not found. Please log in again.');
      return;
    }
    
    // Get customer name and email from profile if available
    const savedProfile = localStorage.getItem('customerProfile');
    let customerName = '';
    let customerEmail = '';
    
    if (savedProfile) {
      try {
        const profile = JSON.parse(savedProfile);
        customerName = profile.name || profile.fullName || '';
        customerEmail = profile.email || '';
      } catch (error) {
        console.error('Error parsing customer profile:', error);
      }
    }
    
    // If name not in profile, try to get from userData
    if (!customerName && currentUser) {
      customerName = currentUser.name || currentUser.full_name || currentUser.email || 'Customer';
      customerEmail = currentUser.email || customerEmail;
    }
    
    // Fallback if still no name
    if (!customerName) {
      customerName = 'Customer';
    }

    // Prepare products data for API
    const productsData = products.map(product => ({
      product_name: product.productName,
      product_category: product.productCategory,
      custom_category: product.productCategory === 'others' ? (product.customCategory || '') : '',
      length_cm: parseFloat(product.length),
      height_cm: parseFloat(product.height),
      width_cm: parseFloat(product.width),
      weight_kg: parseFloat(product.weight)
    }));

    // Prepare booking request data
    const bookingData = {
      customer_id: customerId,
      name: customerName || 'Customer', // Fallback if name not available
      email: customerEmail || '',
      phone: bookingFormData.phoneNumber.replace(/[\s-]/g, ''), // Remove spaces and dashes
      alternate_phone: bookingFormData.alternatePhone ? bookingFormData.alternatePhone.replace(/[\s-]/g, '') : '',
      from_location: searchData.fromLocation,
      from_location_city_id: searchData.fromLocationCityId, // NEW - city ID
      to_location: searchData.toLocation,
      to_location_city_id: searchData.toLocationCityId, // NEW - city ID
      vehicle_type: selectedTruck?.vehicleType || '',
      requested_date: new Date().toISOString(), // Current date/time as requested date
      notes: bookingFormData.specialInstructions || '',
      products: productsData
    };

    setIsSubmittingBooking(true);
    
    try {
      console.log('Submitting booking request:', bookingData);
      const response = await createBookingRequest(bookingData);
      console.log('Booking request created successfully:', response);
      
      // Show success popup
      setShowBookingForm(false);
      setShowBookingSuccess(true);
    } catch (error) {
      console.error('Error creating booking request:', error);
      alert(error.message || 'Failed to submit booking request. Please try again.');
    } finally {
      setIsSubmittingBooking(false);
    }
  };

  const handleCloseBookingForm = () => {
    setShowBookingForm(false);
    setSelectedTruck(null);
    setProducts([{
      productName: '',
      productCategory: '',
      customCategory: '',
      length: '',
      height: '',
      width: '',
      weight: ''
    }]);
    setBookingFormData({
      phoneNumber: getCustomerPhone(),
      alternatePhone: '',
      specialInstructions: ''
    });
    setBookingFormErrors({});
  };

  const handleCloseSuccess = () => {
    setShowBookingSuccess(false);
    setSelectedTruck(null);
  };

  const handleClosePopup = () => {
    setShowBookingPopup(false);
    setSelectedTruck(null);
  };

  return (
    <div className="truck-search-container">
      {/* Search Section */}
      <div className="truck-search-content">
        <div className="search-section">
          <p className="search-subtitle">Enter your pickup and delivery locations to find available trucks</p>
          
          <form className="search-form" onSubmit={handleSearch}>
            <div className="search-inputs">
              <div className="input-group">
                <label className="input-label" htmlFor="fromLocation">
                  <span className="input-label-text">From Location</span>
                </label>
                <div className="input-wrapper location-autocomplete-wrapper">
                  <input
                    type="text"
                    id="fromLocation"
                    name="fromLocation"
                    className="location-input"
                    value={searchData.fromLocation}
                    onChange={handleInputChange}
                    onFocus={() => {
                      if (fromCitySuggestions.length > 0) {
                        setShowFromSuggestions(true);
                      }
                    }}
                    placeholder="Type to search city..."
                    required
                    autoComplete="off"
                  />
                  {showFromSuggestions && fromCitySuggestions.length > 0 && (
                    <div className="city-suggestions-dropdown">
                      {fromCitySuggestions.map((city) => (
                        <div
                          key={city.id}
                          className="city-suggestion-item"
                          onClick={() => handleCitySelect(city, 'from')}
                        >
                          <div className="city-suggestion-name">
                            {city.city}
                          </div>
                          <div className="city-suggestion-details">
                            {city.district && `${city.district}, `}{city.state}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {isSearchingCities && (
                    <div className="city-suggestions-loading">Searching...</div>
                  )}
                </div>
              </div>
              
              <div className="input-group">
                <label className="input-label" htmlFor="toLocation">
                  <span className="input-label-text">To Location</span>
                </label>
                <div className="input-wrapper location-autocomplete-wrapper">
                  <input
                    type="text"
                    id="toLocation"
                    name="toLocation"
                    className="location-input"
                    value={searchData.toLocation}
                    onChange={handleInputChange}
                    onFocus={() => {
                      if (toCitySuggestions.length > 0) {
                        setShowToSuggestions(true);
                      }
                    }}
                    placeholder="Type to search city..."
                    required
                    autoComplete="off"
                  />
                  {showToSuggestions && toCitySuggestions.length > 0 && (
                    <div className="city-suggestions-dropdown">
                      {toCitySuggestions.map((city) => (
                        <div
                          key={city.id}
                          className="city-suggestion-item"
                          onClick={() => handleCitySelect(city, 'to')}
                        >
                          <div className="city-suggestion-name">
                            {city.city}
                          </div>
                          <div className="city-suggestion-details">
                            {city.district && `${city.district}, `}{city.state}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {isSearchingCities && (
                    <div className="city-suggestions-loading">Searching...</div>
                  )}
                </div>
              </div>
            </div>
            
            <button type="submit" className="search-btn" disabled={isSearching}>
              {isSearching ? 'Searching...' : 'Search Trucks'}
            </button>
          </form>
        </div>

        {/* Results Section */}
        {searchResults.length > 0 && (
          <div className="results-section">
            <h2 className="results-title">Available Trucks</h2>
            <div className="trucks-grid">
              {searchResults.map(truck => (
                <div key={truck.id} className="truck-card">
                  <div className="truck-card-header">
                    <div className="truck-id">
                      {truck.fullRoute || `${searchData.fromLocation} → ${searchData.toLocation}`}
                      {(truck.pickupIsIntermediate || truck.deliveryIsIntermediate) && (
                        <span className="intermediate-stop-badge">
                          {truck.pickupIsIntermediate && '📍 Pickup at Stop'}
                          {truck.pickupIsIntermediate && truck.deliveryIsIntermediate && ' • '}
                          {truck.deliveryIsIntermediate && '📍 Delivery at Stop'}
                        </span>
                      )}
                      {truck.isSchedule && (
                        <span className="schedule-badge" style={{ 
                          display: 'block', 
                          marginTop: '4px', 
                          fontSize: '0.75rem', 
                          color: '#7C3AED', 
                          fontWeight: 500 
                        }}>
                          📅 {truck.scheduleType || 'Scheduled'} Route
                        </span>
                      )}
                    </div>
                    <span className={`truck-status ${truck.status.toLowerCase().replace(/\s+/g, '-')}`}>
                      {truck.status}
                    </span>
                  </div>
                  <div className="truck-card-body">
                    {truck.routeDistance && (
                      <div className="truck-info-row">
                        <span className="truck-label">Distance:</span>
                        <span className="truck-value">{truck.routeDistance} km</span>
                      </div>
                    )}
                    <div className="truck-info-row">
                      <span className="truck-label">Vehicle Type:</span>
                      <span className="truck-value">{getVehicleTypeDisplay(truck.vehicleType)}</span>
                    </div>
                    <div className="truck-info-row">
                      <span className="truck-label">Capacity:</span>
                      <span className="truck-value">{truck.capacity}</span>
                    </div>
                  </div>
                  <div className="truck-card-footer">
                    <button className="book-btn" onClick={() => handleBookTruck(truck)}>
                      Book This Truck
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {searchResults.length === 0 && !isSearching && (
          <div className="no-results">
            <p>Enter locations and click "Search Trucks" to find available vehicles</p>
          </div>
        )}
      </div>

      {/* Booking Form Modal */}
      {showBookingForm && selectedTruck && (
        <div className="booking-form-overlay" onClick={handleCloseBookingForm}>
          <div className="booking-form-content" onClick={(e) => e.stopPropagation()}>
            <div className="booking-form-header">
              <h2 className="booking-form-title">Book Truck - {selectedTruck.vehicleType}</h2>
              <button className="booking-form-close" onClick={handleCloseBookingForm}>×</button>
            </div>

            <div className="booking-form-body">
              {/* Product Details Form */}
              <form className="booking-details-form" onSubmit={handleBookingSubmit}>
                <div className="products-section-header">
                  <h3 className="booking-section-title">Product Details</h3>
                  <button 
                    type="button" 
                    className="add-product-btn" 
                    onClick={handleAddProduct}
                    title="Add another product"
                  >
                    <span className="add-product-icon">+</span> Additional Product
                  </button>
                </div>

                {products.map((product, index) => (
                  <div key={index} className="product-item">
                    <div className="product-item-header">
                      <h4 className="product-item-title">Product {index + 1}</h4>
                      {products.length > 1 && (
                        <button 
                          type="button" 
                          className="remove-product-btn" 
                          onClick={() => handleRemoveProduct(index)}
                          title="Remove this product"
                        >
                          ×
                        </button>
                      )}
                    </div>

                    <div className="booking-form-row">
                      <div className="booking-form-group">
                        <label className="booking-form-label">
                          Product Name <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          className={`booking-form-input ${bookingFormErrors[`product_${index}_productName`] ? 'error' : ''}`}
                          value={product.productName}
                          onChange={(e) => handleProductChange(index, 'productName', e.target.value)}
                          placeholder="Enter product name"
                        />
                        {bookingFormErrors[`product_${index}_productName`] && (
                          <span className="booking-field-error">{bookingFormErrors[`product_${index}_productName`]}</span>
                        )}
                      </div>

                      <div className="booking-form-group">
                        <label className="booking-form-label">
                          Product Category <span className="required">*</span>
                        </label>
                        <select
                          className={`booking-form-input ${bookingFormErrors[`product_${index}_productCategory`] ? 'error' : ''}`}
                          value={product.productCategory}
                          onChange={(e) => {
                            const newCategory = e.target.value;
                            const updatedProducts = [...products];
                            updatedProducts[index] = {
                              ...updatedProducts[index],
                              productCategory: newCategory,
                              customCategory: newCategory === 'others' ? updatedProducts[index].customCategory : ''
                            };
                            setProducts(updatedProducts);
                            // Clear errors
                            const errorKey = `product_${index}_productCategory`;
                            if (bookingFormErrors[errorKey]) {
                              setBookingFormErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[errorKey];
                                if (newCategory !== 'others') {
                                  delete newErrors[`product_${index}_customCategory`];
                                }
                                return newErrors;
                              });
                            }
                          }}
                        >
                          <option value="">Select category</option>
                          {productCategories.map(category => (
                            <option key={category} value={category}>
                              {category.charAt(0).toUpperCase() + category.slice(1)}
                            </option>
                          ))}
                        </select>
                        {bookingFormErrors[`product_${index}_productCategory`] && (
                          <span className="booking-field-error">{bookingFormErrors[`product_${index}_productCategory`]}</span>
                        )}
                        {product.productCategory === 'others' && (
                          <div className="booking-form-group" style={{ marginTop: '12px' }}>
                            <label className="booking-form-label">
                              Specify Category <span className="required">*</span>
                            </label>
                            <input
                              type="text"
                              className={`booking-form-input ${bookingFormErrors[`product_${index}_customCategory`] ? 'error' : ''}`}
                              value={product.customCategory}
                              onChange={(e) => handleProductChange(index, 'customCategory', e.target.value)}
                              placeholder="Enter custom category"
                            />
                            {bookingFormErrors[`product_${index}_customCategory`] && (
                              <span className="booking-field-error">{bookingFormErrors[`product_${index}_customCategory`]}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="booking-form-row">
                      <div className="booking-form-group">
                        <label className="booking-form-label">
                          Length (cm) <span className="required">*</span>
                        </label>
                        <input
                          type="number"
                          className={`booking-form-input ${bookingFormErrors[`product_${index}_length`] ? 'error' : ''}`}
                          value={product.length}
                          onChange={(e) => handleProductChange(index, 'length', e.target.value)}
                          placeholder="Enter length"
                          min="0.01"
                          step="0.01"
                        />
                        {bookingFormErrors[`product_${index}_length`] && (
                          <span className="booking-field-error">{bookingFormErrors[`product_${index}_length`]}</span>
                        )}
                      </div>

                      <div className="booking-form-group">
                        <label className="booking-form-label">
                          Height (cm) <span className="required">*</span>
                        </label>
                        <input
                          type="number"
                          className={`booking-form-input ${bookingFormErrors[`product_${index}_height`] ? 'error' : ''}`}
                          value={product.height}
                          onChange={(e) => handleProductChange(index, 'height', e.target.value)}
                          placeholder="Enter height"
                          min="0.01"
                          step="0.01"
                        />
                        {bookingFormErrors[`product_${index}_height`] && (
                          <span className="booking-field-error">{bookingFormErrors[`product_${index}_height`]}</span>
                        )}
                      </div>

                      <div className="booking-form-group">
                        <label className="booking-form-label">
                          Width (cm) <span className="required">*</span>
                        </label>
                        <input
                          type="number"
                          className={`booking-form-input ${bookingFormErrors[`product_${index}_width`] ? 'error' : ''}`}
                          value={product.width}
                          onChange={(e) => handleProductChange(index, 'width', e.target.value)}
                          placeholder="Enter width"
                          min="0.01"
                          step="0.01"
                        />
                        {bookingFormErrors[`product_${index}_width`] && (
                          <span className="booking-field-error">{bookingFormErrors[`product_${index}_width`]}</span>
                        )}
                      </div>

                      <div className="booking-form-group">
                        <label className="booking-form-label">
                          Weight (kg) <span className="required">*</span>
                        </label>
                        <input
                          type="number"
                          className={`booking-form-input ${bookingFormErrors[`product_${index}_weight`] ? 'error' : ''}`}
                          value={product.weight}
                          onChange={(e) => handleProductChange(index, 'weight', e.target.value)}
                          placeholder="Enter weight"
                          min="0.01"
                          step="0.01"
                        />
                        {bookingFormErrors[`product_${index}_weight`] && (
                          <span className="booking-field-error">{bookingFormErrors[`product_${index}_weight`]}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                <h3 className="booking-section-title" style={{ marginTop: '24px' }}>Contact Information</h3>

                <div className="booking-form-row">
                  <div className="booking-form-group">
                    <label htmlFor="phoneNumber" className="booking-form-label">
                      Phone Number <span className="required">*</span>
                      <span className="phone-hint">(Auto-filled from profile)</span>
                    </label>
                    <input
                      type="tel"
                      id="phoneNumber"
                      name="phoneNumber"
                      className={`booking-form-input ${bookingFormErrors.phoneNumber ? 'error' : ''}`}
                      value={bookingFormData.phoneNumber}
                      onChange={handleBookingFormChange}
                      placeholder="Enter 10-digit phone number"
                      maxLength="10"
                    />
                    {bookingFormErrors.phoneNumber && (
                      <span className="booking-field-error">{bookingFormErrors.phoneNumber}</span>
                    )}
                  </div>

                  <div className="booking-form-group">
                    <label htmlFor="alternatePhone" className="booking-form-label">
                      Alternate Phone (Optional)
                    </label>
                    <input
                      type="tel"
                      id="alternatePhone"
                      name="alternatePhone"
                      className={`booking-form-input ${bookingFormErrors.alternatePhone ? 'error' : ''}`}
                      value={bookingFormData.alternatePhone}
                      onChange={handleBookingFormChange}
                      placeholder="Enter alternate phone number"
                      maxLength="10"
                    />
                    {bookingFormErrors.alternatePhone && (
                      <span className="booking-field-error">{bookingFormErrors.alternatePhone}</span>
                    )}
                  </div>
                </div>

                <div className="booking-form-row">
                  <div className="booking-form-group">
                    <label htmlFor="specialInstructions" className="booking-form-label">
                      Special Instructions (Optional)
                    </label>
                    <textarea
                      id="specialInstructions"
                      name="specialInstructions"
                      className="booking-form-input"
                      value={bookingFormData.specialInstructions}
                      onChange={handleBookingFormChange}
                      placeholder="Any special instructions for the driver or delivery"
                      rows="3"
                    />
                  </div>
                </div>

                <div className="booking-form-actions">
                  <button 
                    type="button" 
                    className="booking-cancel-btn" 
                    onClick={handleCloseBookingForm}
                    disabled={isSubmittingBooking}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="booking-submit-btn"
                    disabled={isSubmittingBooking}
                  >
                    {isSubmittingBooking ? 'Submitting...' : 'Confirm Booking'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Booking Success Popup */}
      {showBookingSuccess && selectedTruck && (
        <div className="booking-popup-overlay" onClick={handleCloseSuccess}>
          <div className="booking-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="booking-popup-header">
              <h2 className="booking-popup-title">Booking Confirmed!</h2>
              <button className="booking-popup-close" onClick={handleCloseSuccess}>×</button>
            </div>
            <div className="booking-popup-body">
              <div className="booking-popup-icon">✅</div>
              <h3 className="booking-popup-message">Booking Request Submitted Successfully!</h3>
              <div className="booking-popup-details">
                <p className="booking-popup-text">
                  Your booking request for <strong>{selectedTruck.vehicleNumber}</strong> has been received.
                </p>
                <div className="booking-summary">
                  <p><strong>Products:</strong> {products.length} item(s)</p>
                  {products.map((product, index) => (
                    <div key={index} className="product-summary-item">
                      <p><strong>Product {index + 1}:</strong> {product.productName} ({product.productCategory === 'others' ? product.customCategory : product.productCategory})</p>
                      <p style={{ marginLeft: '20px', fontSize: '0.9rem', color: '#666' }}>
                        Size: {product.length}cm × {product.height}cm × {product.width}cm | Weight: {product.weight} kg
                      </p>
                    </div>
                  ))}
                  <p><strong>Route:</strong> {selectedTruck?.fullRoute || `${searchData.fromLocation} → ${searchData.toLocation}`}</p>
                  {(selectedTruck?.pickupIsIntermediate || selectedTruck?.deliveryIsIntermediate) && (
                    <p style={{ fontSize: '0.9rem', color: '#666', fontStyle: 'italic', marginTop: '4px' }}>
                      {selectedTruck.pickupIsIntermediate && '📍 Pickup at intermediate stop'}
                      {selectedTruck.pickupIsIntermediate && selectedTruck.deliveryIsIntermediate && ' • '}
                      {selectedTruck.deliveryIsIntermediate && '📍 Delivery at intermediate stop'}
                    </p>
                  )}
                  <p><strong>Contact:</strong> {bookingFormData.phoneNumber}</p>
                </div>
                <p className="booking-popup-text" style={{ marginTop: '15px' }}>
                  Our team will contact you soon to confirm your booking.
                </p>
              </div>
            </div>
            <div className="booking-popup-footer">
              <button className="booking-popup-ok-btn" onClick={handleCloseSuccess}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TruckSearch;

